/**
 * The pageCache mixin stores all of the values of the data() property on a Vue component when the page/component is destroyed
 * and reloads them when the page/component is created. These properties are stored for the live of the tab/browser in 
 * SessionStorage.
 * 
 * BASIC USAGE
 * 
 * import { pageCache } from '@/mixins/page-cache.js'
 * 
 * // then in the Vue component
 * mixins: [pageCache('tracking')],
 * 
 * Any additional operations that need to be performed to set up a component/page after the data has been loaded can be done by
 * overiding the function afterDataLoadHandler(data) in the component using the mixin. 
 */

import { Logger } from "aws-amplify"
const logger = new Logger("PAGE CACHE")

export const pageCache = (componentName = false, propertiesNotToLoad = ['loading', 'loaded', 'dataLoaded']) => ({

  data () {
    return {
      // sometimes we don't want to cache a property, this is usually for state management such a loading variables etc
      // a list can be passed on creation to this component if there are additional state keeping properties you do not want to be cached
      PROPERTIES_NOT_TO_CACHE: propertiesNotToLoad
    }  
  },
  // when the component is mounted, we run our load handler to check for data in storage
  beforeMount () {
    if(componentName) {
      this.loadPageHandler()
    } 
  },

  // we save our data to storage while it still exists
  beforeDestroy () {
    if(componentName) {
      this.savePageHandler()
    } 
  },

  methods: {
    // this can be overridden if additional operations are required by the page after the data has been loaded. 
    // the data loaded from storage is passed to this function
    afterDataLoadHandler (data = false) {
      logger.debug("default after load handler", data)
    },

    afterDataSaveHandler (data) {
      logger.debug("default after save handler", data)
    },

    // called automatically on page destroy, this saves all of the pages data
    savePageHandler () {
      if(componentName) {
        logger.debug("page data saved", this._data)
        this.savePageData(componentName, this._data)

        // call our after save function 
        this.afterDataSaveHandler(this._data)
      }
    },

    // called automatically on page load, this loads all of the saved page data
    async loadPageHandler () {
      logger.debug("page data loaded handler called")
      if(componentName) {
        try {
          const pageData = await this.loadPageData(componentName)

          // iterate through each property on the stored data object and set the properties on the current Vue component
          Object.keys(pageData.data).forEach(property => {
            if(!this.PROPERTIES_NOT_TO_CACHE.includes(property))
              this[property] = pageData.data[property] 
          })

          // we pass the page data to the afterDataLoadHandler, any extra operations required by the component can be performed here
          this.afterDataLoadHandler(pageData.data)

          logger.debug("page data loaded", pageData)
        } catch (err) {
          logger.debug("page data loaded error", err)
        }
      }
    },

    /**
     * saves an object to sessionStorage under a given pageTitle (key)
     * @param {string} pageTitle - key to save the pages data under
     * @param {Object} data - an object representing the pages data
     * @returns {Promise}
     */
    savePageData (pageTitle = false, data = false) {
      return new Promise(res => {
        try {
          if(!pageTitle || !data) {
            res (true)
          } else {
            // save process here
            window.sessionStorage.setItem(pageTitle, JSON.stringify(data))
          }
        } catch (err) {
          logger.debug("saving page data failed", err)
        }
      })
    },

    /**
     * loads an object from sessionStorage under a given pageTitle (key). Returns the object after loading
     * @param {string} pageTitle - key to load from storage
     * @returns {Promise}
     */
    loadPageData (pageTitle = false) {
      return new Promise(res => {
        try {
          if(!pageTitle) {
            res (true)
          }
          // load process here
          const data = window.sessionStorage.getItem(pageTitle)
          res({ 
            title: pageTitle,
            data: JSON.parse(data) || {}
          })  
        } catch (err) {
          logger.debug("loading page Data failed", err)
        }
      })
    },
  }
})